import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  linkedSignal,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, switchMap } from 'rxjs';
import {
  API,
  CompanyDTO,
  CompanyFullDTO,
  MediumFolderDTO,
  MediumGroupCategoryDTO,
  MediumGroupDTO,
  MediumGroupFolderDTO,
  MediumGroupFullDTO,
  mm_MediumGroup_retrieveAll_order,
  Order,
  Pagination,
} from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { UtilService } from '../../../services/util.service';
import { CompaniesComponent } from '../../company/companies/companies.component';
import { UserService } from '../../../services/user.service';
import { TagService } from '../../../services/tag.service';
import { MediumGroupFullDTOAndTags } from '../../../models/taggable.model';
import { NavigationService } from '@ngx-seatback/utils';
import { DragItemStyle, MsOptions, OrderOption, SbSplitButton, SplitButtonClass } from '../../../services/global/global-interfaces';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { StringTransformationService } from 'src/app/services/transformation-helper/string-transformation.service';
import { RequestClass } from '../../../services/global/RequestClass';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButtonItem, SearchOptions, SearchType } from '@ngx-seatback/controls';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { MediumViewerComponent } from '../../../components/controls/medium-viewer/medium-viewer.component';

type SplitButtonAction = 'delete';
type MediumGroupFullDTOAndTagsAndButtons = SplitButtonClass<MediumGroupFullDTOAndTags, SplitButtonAction>;

@Component({
  selector: 'sb-page-medium-groups',
  templateUrl: './medium-groups.component.html',
  styleUrls: ['./medium-groups.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediumGroupsComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  // injects
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly api = inject(API);
  protected readonly modalService = inject(ModalService);
  protected readonly modalNavigation = inject(ModalNavigationService);
  protected readonly errorHandler = inject(ErrorService);
  protected readonly util = inject(UtilService);
  protected readonly translate = inject(TranslateService);
  protected readonly userService = inject(UserService);
  protected readonly tagService = inject(TagService);
  protected readonly navService = inject(NavigationService);
  protected readonly elementRef = inject(ElementRef);
  protected readonly toaster = inject(ToasterService);
  protected readonly permissionService = inject(PermissionService);
  protected readonly cdRef = inject(ChangeDetectorRef);

  // global reference imports
  protected readonly window = window;
  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;
  protected readonly mm_MediumGroup_retrieveAll_order = mm_MediumGroup_retrieveAll_order;

  @Input() isInModalContext: boolean;

  /* filter variables */
  // search
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  // Id filtering
  selectedCategories: MediumGroupCategoryDTO[] = [];
  selectedCompany: CompanyDTO | null = null;

  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  /* ordering */
  orderOption = signal<OrderOption<mm_MediumGroup_retrieveAll_order>>({
    direction: Order.asc,
    value: mm_MediumGroup_retrieveAll_order.CREATED_AT,
  });
  filterPanelOrderOption = linkedSignal<OrderOption<mm_MediumGroup_retrieveAll_order>, OrderOption<mm_MediumGroup_retrieveAll_order>>({
    source: this.orderOption,
    computation: orderOption => {
      return { ...orderOption };
    },
  });
  allOrderOptions: OrderOption<mm_MediumGroup_retrieveAll_order>[] = [
    { value: mm_MediumGroup_retrieveAll_order.CREATED_AT, direction: Order.desc, label: this.translate.instant('lang.globals.createdAt') },
    { value: mm_MediumGroup_retrieveAll_order.NAME, direction: Order.desc, label: this.translate.instant('lang.globals.name') },
    { value: mm_MediumGroup_retrieveAll_order.COMPANY, direction: Order.desc, label: this.translate.instant('lang.globals.company') },
    {
      value: mm_MediumGroup_retrieveAll_order.MODIFIED_AT,
      direction: Order.desc,
      label: this.translate.instant('lang.globals.modifiedAt'),
    },
  ];

  /* global */
  showSideBar: boolean = false;
  mediumGroups = signal<MediumGroupFullDTOAndTagsAndButtons[]>([]);

  // dragging
  dragging: MediumGroupFullDTOAndTagsAndButtons;
  dragItemStyle: DragItemStyle;
  folderDragoverId: number | null;

  // folder
  @ViewChild('folderInput') folderInput: ElementRef;
  folderPagination: BehaviorSubject<Pagination> = new BehaviorSubject<Pagination>({
    page: 1,
    count: 99,
    pages: -1,
    resultcount: -1,
  });
  folderString: string = '';
  folderSearch: string = '';
  folderExactMatch: SearchOptions = 'contains';
  mediumGroupFolders: MediumGroupFolderDTO[] = [];
  selectedFolder: MediumGroupFolderDTO | null = null;
  folderName: string | null = null;
  folderCreationMode: boolean = false;
  folderCreationInProgress: boolean = false;
  customer: CompanyDTO | null = null;
  protected folderView: boolean = false;

  constructor() {
    super();
    this.resetOrderOption();
  }

  ngOnInit() {
    this.getFolders();
    this.userService.selectedCompany.subscribe((company: CompanyDTO) => {
      if (company) {
        this.selectedCompany = company;
        this.requestDebounced();
      }
    });
    if (this.isInModalContext) {
      this.filterConfig = { ...this.filterConfig, ignoreQueryParams: true };
      if (this.modalNavigation.getCurrentNavigation().data.customer) {
        this.customer = this.modalNavigation.getCurrentNavigation().data.customer;
        this.selectedCompany = this.modalNavigation.getCurrentNavigation().data.customer;
      }
    }
  }

  // order methods
  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({ ...this.allOrderOptions[0] });
  }

  setToOrderAndRefresh(pValue: mm_MediumGroup_retrieveAll_order) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  setToOrderFilterPanel(pValue: mm_MediumGroup_retrieveAll_order) {
    this.filterPanelOrderOption.update(orderOption => {
      orderOption.value = pValue;
      return { ...orderOption };
    });
    if (this.filterPanelRef) {
      this.filterPanelRef.hasExternalChanges.set(true);
    }
  }

  setOrderDirectionFilterPanel(value: Order) {
    this.filterPanelOrderOption.update(orderOption => {
      orderOption.direction = value;
      return { ...orderOption };
    });
    if (this.filterPanelRef) {
      this.filterPanelRef.hasExternalChanges.set(true);
    }
  }

  private setToOrderOption(pValue: mm_MediumGroup_retrieveAll_order) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ ...this.allOrderOptions.find(option => (option.value = pValue)) });
    }
  }

  getOrder(): { by: mm_MediumGroup_retrieveAll_order; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value) {
      this.resetOrderOption();
      return {
        by: mm_MediumGroup_retrieveAll_order.CREATED_AT,
        direction: Order.desc,
      };
    }
    return { by: currentOrderOption.value, direction: currentOrderOption.direction };
  }

  // request
  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const predefinedCustomer = this.customer?.id ?? this.selectedCompany?.id ?? null;
    const selectedCategories = this.selectedCategories?.length > 0 ? this.selectedCategories.map(cat => cat?.id) : null;
    const sub = this.api
      .mm_MediumGroup_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          order: this.getOrder(),
          folder_id: this.folderView && this.selectedFolder ? this.selectedFolder.id : null,
          hasFolder: this.folderView ? !!this.selectedFolder : null,
          company: predefinedCustomer ? [predefinedCustomer] : null,
          category: selectedCategories,
        },
        this.pagination
      )
      .pipe(
        switchMap(groups => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.MediumGroup, groups)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe((data: MediumGroupFullDTO[]) => {
        this.mediumGroups.set(this.buildSplitButtons(data));
      });
    this.subs.push(sub);
  }

  buildSplitButtons<
    T extends {
      id: any;
    },
  >(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.medium_group_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.mediumGroupEdit,
          pathParams: [{ id: 'mediumGroupID', value: item?.id }],
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.medium_group_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.delete',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(group: MediumGroupFullDTOAndTagsAndButtons, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.deleteMediumGroup(group);
        break;

      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  selectGroup(mediumGroup: MediumGroupFullDTO) {
    if (this.isInModalContext) {
      this.modalNavigation.result(mediumGroup);
    }
  }

  toggleSideBar() {
    this.showSideBar = !this.showSideBar;
  }

  handleMediumGroupChanged($event: MediumGroupFullDTO) {
    if ($event) {
      this.requestDebounced();
      this.getFolders();
    }
    this.toggleSideBar();
  }

  /* dragging events */
  moveDragElement(event: DragEvent) {
    if (this.dragging) {
      this.dragItemStyle.left = event.pageX + 25;
      this.dragItemStyle.top = event.pageY - 50;
    }
  }

  dragStart($event: any) {
    const event: DragEvent = $event.event;
    this.dragging = $event.item;
    if (event.dataTransfer.setDragImage) {
      const dragDummy = (this.elementRef.nativeElement as Element).querySelector('.drag-dummy');
      event.dataTransfer.setDragImage(dragDummy, 0, 0);
    }
    this.dragItemStyle = {
      left: event.screenX,
      top: event.screenY,
    };
  }

  dragEnd() {
    this.dragging = undefined;
  }

  dragOver(event: DragEvent, folderId: number) {
    if (this.dragging) {
      event.preventDefault();
      this.folderDragoverId = folderId;
    }
  }

  dragOut(event: DragEvent) {
    event.preventDefault();
    this.folderDragoverId = null;
  }

  drop(event: DragEvent, folder: MediumGroupFolderDTO) {
    if (!this.dragging) {
      return;
    }
    this.moveToFolder(folder, this.dragging);
  }

  /* folder */

  doFolderSearch(searchObj: SearchType) {
    this.folderSearch = searchObj.searchTerm;
    this.folderExactMatch = searchObj.exactMatch;
    this.getFolders();
  }

  getFolders() {
    this.api
      .mm_MediumGroupFolder_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.folderExactMatch, this.folderSearch),
        },
        this.folderPagination
      )
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (data: MediumGroupFolderDTO[]) => {
          if (!data) {
            data = [];
          }
          this.mediumGroupFolders = data;
          this.folderString = this.stringTransformation.getPaginationString(
            this.folderPagination.getValue().page,
            this.folderPagination.getValue().count,
            this.mediumGroupFolders.length,
            this.folderPagination.getValue().resultcount,
            'folder'
          );
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  moveToFolder(folder: MediumGroupFolderDTO, mediumGroup: MediumGroupFullDTO) {
    mediumGroup.folder = folder;
    this.api
      .mm_MediumGroup_edit(mediumGroup)
      .pipe(
        take(1),
        finalize(() => {
          this.dragging = undefined;
          this.folderDragoverId = null;
        })
      )
      .subscribe({
        next: () => {
          this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.itemMovedToFolder', { itemName: mediumGroup.name }));
          this.requestDebounced();
          this.getFolders();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  removeFromFolder(folderId: number, mediumGroup: MediumGroupDTO) {
    this.api
      .mm_MediumGroupFolder_moveToRoot(mediumGroup.id)
      .pipe(
        take(1),
        finalize(() => {
          this.dragging = undefined;
          this.folderDragoverId = null;
        })
      )
      .subscribe({
        next: () => {
          this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.itemMovedToFolder', { itemName: mediumGroup.name }));
          this.requestDebounced();
          this.getFolders();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  toggleFolderCreation($event?: MouseEvent) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.folderCreationMode = !this.folderCreationMode;
    if (this.folderCreationMode && this.folderInput) {
      setTimeout(() => {
        this.folderInput.nativeElement.focus();
      }, 100);
    }
  }

  toggleFolder(folder: MediumGroupFolderDTO | null, folderView: boolean = true) {
    this.selectedFolder = folder;
    this.folderView = folderView;
    this.requestDebounced();
  }

  createNewFolder() {
    if (!this.folderName) {
      return;
    }
    this.folderCreationInProgress = true;
    const folder: MediumGroupFolderDTO = new MediumGroupFolderDTO();
    folder.name = this.folderName;
    this.api
      .mm_MediumGroupFolder_createRootFolder(folder)
      .pipe(
        take(1),
        finalize(() => {
          this.folderName = null;
          this.folderCreationMode = false;
          this.folderCreationInProgress = false;
          this.toggleFolderCreation();
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: data => {
          this.pushToFolders(data);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  deleteFolder($event: MouseEvent, folder: MediumGroupFolderDTO) {
    $event.preventDefault();
    $event.stopPropagation();
    if (!folder) {
      return;
    }
    if (folder.elementsCount > 0) {
      this.toaster.toast(ToastType.DANGER, this.translate.instant('lang.attachment.deleteFolderSizeWarning'));
      return;
    }
    if (folder.elementsCount === 0) {
      this.modalService
        .confirm({
          title: this.translate.instant('lang.attachment.deleteFolder'),
          message: this.translate.instant('lang.attachment.deleteFolderMessage', { itemName: folder.name }),
          yes: this.translate.instant('lang.globals.yes'),
          no: this.translate.instant('lang.globals.no'),
        })
        .then((result: boolean) => {
          if (result) {
            this.api
              .mm_MediumGroupFolder_deleteById(folder.id)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: () => {
                  this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.deleteSuccess'));
                  this.getFolders();
                },
              });
          }
        });
    }
  }

  pushToFolders(folder: MediumGroupFolderDTO) {
    this.mediumGroupFolders.unshift(folder);
    this.toaster.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.successful'));
  }

  // delete MediumGroup
  deleteMediumGroup(mediumGroup: MediumGroupFullDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.deleteElementValue', { value: mediumGroup.name }),
        message: this.translate.instant('lang.globals.deleteElementValueQuestion', { value: mediumGroup.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .mm_MediumGroup_deleteMediumGroup(mediumGroup.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.requestDebounced();
                this.getFolders();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  // filter panel methods
  checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  openFilterPanel(elementId: HTMLDivElement) {
    if (!elementId) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(elementId, false);
    }
  }

  scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.selectedCompany = filterMap.get('companies')?.[0];
    this.selectedCategories = filterMap.get('mediumGroupCategories');
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.orderOption.set({ ...this.filterPanelOrderOption() });
  }
}
